
import { SubscriptionStatus, SubscriptionCache, LastFetchTimes, PendingRequests } from './types';

// Track the last time we fetched each user's subscription status
export const lastFetchTimes: LastFetchTimes = {};

// Cache subscription status to reduce flicker
export const subscriptionCache: SubscriptionCache = {};

// Track in-flight requests to prevent duplicates
export const pendingRequests: PendingRequests = {};

/**
 * Store subscription status in both memory and localStorage
 */
export const cacheSubscriptionStatus = (
  userId: string, 
  status: SubscriptionStatus,
  timestamp: number = Date.now()
): void => {
  // Cache in memory
  subscriptionCache[userId] = {
    status,
    timestamp
  };
  
  // Cache in localStorage
  localStorage.setItem(`subscription_status_${userId}`, JSON.stringify(status));
  
  // If subscription is active, set the confirmed flag
  if (status.isActive) {
    localStorage.setItem('subscription_confirmed_active', 'true');
    // Notify other components
    window.dispatchEvent(new Event('subscription_updated'));
  }
};

/**
 * Get cached subscription status if available and not expired
 */
export const getCachedStatus = (
  userId: string,
  maxAge: number = 10000 // 10 seconds default
): SubscriptionStatus | null => {
  const now = Date.now();
  const cacheEntry = subscriptionCache[userId];
  
  if (cacheEntry && now - cacheEntry.timestamp < maxAge) {
    return cacheEntry.status;
  }
  
  // Try localStorage if no memory cache
  const cachedStatus = localStorage.getItem(`subscription_status_${userId}`);
  if (cachedStatus) {
    return JSON.parse(cachedStatus);
  }
  
  return null;
};

/**
 * Clear cached subscription status for a user
 */
export const clearCache = (userId: string): void => {
  delete subscriptionCache[userId];
  delete pendingRequests[userId];
};
